// src/pages/Profile/components/ProfileForm.jsx
import React, { useState } from 'react';

const ProfileForm = ({ profile, onSave, onCancel }) => {
    const [formData, setFormData] = useState({ ...profile });
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    
    // Local state for skills and languages (to handle commas properly)
    const [skillsInput, setSkillsInput] = useState(
        Array.isArray(profile.skills) ? profile.skills.join(', ') : ''
    );
    const [languagesInput, setLanguagesInput] = useState(
        Array.isArray(profile.languages) ? profile.languages.join(', ') : ''
    );

    const experienceLevels = [
        { value: 'NO_EXPERIENCE', label: 'No Experience' },
        { value: 'ZERO_TO_ONE_YEAR', label: '0 - 1 Year' },
        { value: 'ONE_TO_TWO_YEARS', label: '1 - 2 Years' }
    ];

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Skills and Languages handlers
    const handleSkillsChange = (e) => {
        const value = e.target.value;
        setSkillsInput(value);
        const skills = value.split(',').map(s => s.trim()).filter(Boolean);
        setFormData(prev => ({ ...prev, skills }));
    };

    const handleLanguagesChange = (e) => {
        const value = e.target.value;
        setLanguagesInput(value);
        const languages = value.split(',').map(s => s.trim()).filter(Boolean);
        setFormData(prev => ({ ...prev, languages }));
    };

    // Education Functions
    const addEducation = () => {
        setFormData(prev => ({
            ...prev,
            education: [...(prev.education || []), {
                id: Date.now().toString(),
                institution: '',
                qualification: '',
                startDate: '',
                endDate: '',
                isCurrent: false,
                keyHighlights: ''
            }]
        }));
    };

    const updateEducation = (index, field, value) => {
        const newEducation = [...(formData.education || [])];
        newEducation[index][field] = value;
        setFormData(prev => ({ ...prev, education: newEducation }));
    };

    const removeEducation = (index) => {
        setFormData(prev => ({
            ...prev,
            education: prev.education.filter((_, i) => i !== index)
        }));
    };

    // Work Experience Functions
    const addWorkExperience = () => {
        setFormData(prev => ({
            ...prev,
            workExperience: [...(prev.workExperience || []), {
                id: Date.now().toString(),
                company: '',
                title: '',
                startDate: '',
                endDate: '',
                isCurrent: false,
                description: ''
            }]
        }));
    };

    const updateWorkExperience = (index, field, value) => {
        const newExperience = [...(formData.workExperience || [])];
        newExperience[index][field] = value;
        setFormData(prev => ({ ...prev, workExperience: newExperience }));
    };

    const removeWorkExperience = (index) => {
        setFormData(prev => ({
            ...prev,
            workExperience: prev.workExperience.filter((_, i) => i !== index)
        }));
    };

    // Certifications Functions
    const addCertification = () => {
        setFormData(prev => ({
            ...prev,
            certifications: [...(prev.certifications || []), {
                id: Date.now().toString(),
                name: '',
                issuingOrganization: '',
                year: '',
                description: ''
            }]
        }));
    };

    const updateCertification = (index, field, value) => {
        const newCertifications = [...(formData.certifications || [])];
        newCertifications[index][field] = value;
        setFormData(prev => ({ ...prev, certifications: newCertifications }));
    };

    const removeCertification = (index) => {
        setFormData(prev => ({
            ...prev,
            certifications: prev.certifications.filter((_, i) => i !== index)
        }));
    };

    // References Functions
    const addReference = () => {
        setFormData(prev => ({
            ...prev,
            references: [...(prev.references || []), {
                id: Date.now().toString(),
                name: '',
                title: '',
                company: '',
                email: '',
                phone: ''
            }]
        }));
    };

    const updateReference = (index, field, value) => {
        const newReferences = [...(formData.references || [])];
        newReferences[index][field] = value;
        setFormData(prev => ({ ...prev, references: newReferences }));
    };

    const removeReference = (index) => {
        setFormData(prev => ({
            ...prev,
            references: prev.references.filter((_, i) => i !== index)
        }));
    };

    const validate = () => {
        const newErrors = {};
        
        if (!formData.firstName?.trim()) newErrors.firstName = 'First Name is required';
        if (!formData.lastName?.trim()) newErrors.lastName = 'Last Name is required';
        if (!formData.phoneNumber?.trim()) newErrors.phoneNumber = 'Phone Number is required';
        if (!formData.city?.trim()) newErrors.city = 'City is required';
        if (!formData.country?.trim()) newErrors.country = 'Country is required';
        if (!formData.gender) newErrors.gender = 'Gender is required';
        if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of Birth is required';
        
        if (formData.citizenship === 'South African') {
            if (!formData.idNumber?.trim()) {
                newErrors.idNumber = 'ID Number is required for South African citizens';
            } else if (!/^\d{13}$/.test(formData.idNumber)) {
                newErrors.idNumber = 'ID Number must be exactly 13 digits';
            }
        }
        
        if (!formData.lookingFor?.trim()) newErrors.lookingFor = 'What are you looking for? is required';
        if (!formData.preferredJobLocation?.trim()) newErrors.preferredJobLocation = 'Preferred Job Location is required';
        if (!formData.desiredSalary?.trim()) newErrors.desiredSalary = 'Desired Salary is required';
        if (!formData.professionalSummary?.trim()) newErrors.professionalSummary = 'Professional Summary is required';
        if (formData.skills?.length < 3) newErrors.skills = 'Please add at least 3 skills';
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            const firstError = document.querySelector('.border-red-500');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }
        
        setSaving(true);
        await onSave(formData);
        setSaving(false);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <h2 className="text-2xl font-bold text-gradpath-dark">Edit Profile</h2>

            {/* Email - Read Only */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                    <i className="fas fa-envelope text-blue-500"></i>
                    <div>
                        <p className="text-sm text-gray-500">Email Address</p>
                        <p className="font-medium text-gray-800">{formData.email}</p>
                        <p className="text-xs text-gray-400">Email cannot be changed</p>
                    </div>
                </div>
            </div>

            {/* Name Fields */}
            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="firstName"
                        value={formData.firstName || ''}
                        onChange={handleChange}
                        className={`w-full p-3 border rounded-lg focus:outline-none ${
                            errors.firstName ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gradpath-green'
                        }`}
                    />
                    {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="lastName"
                        value={formData.lastName || ''}
                        onChange={handleChange}
                        className={`w-full p-3 border rounded-lg focus:outline-none ${
                            errors.lastName ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gradpath-green'
                        }`}
                    />
                    {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                </div>
            </div>

            {/* Contact */}
            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber || ''}
                        onChange={handleChange}
                        className={`w-full p-3 border rounded-lg focus:outline-none ${
                            errors.phoneNumber ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gradpath-green'
                        }`}
                    />
                    {errors.phoneNumber && <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Job Title</label>
                    <input
                        type="text"
                        name="currentJobTitle"
                        value={formData.currentJobTitle || ''}
                        onChange={handleChange}
                        placeholder="e.g. Software Developer"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                    />
                </div>
            </div>

            {/* Address */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                <input
                    type="text"
                    name="streetAddress"
                    value={formData.streetAddress || ''}
                    onChange={handleChange}
                    placeholder="123 Main Street"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                    <input
                        type="text"
                        name="postcode"
                        value={formData.postcode || ''}
                        onChange={handleChange}
                        placeholder="2001"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        City <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="city"
                        value={formData.city || ''}
                        onChange={handleChange}
                        className={`w-full p-3 border rounded-lg focus:outline-none ${
                            errors.city ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gradpath-green'
                        }`}
                    />
                    {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    name="country"
                    value={formData.country || ''}
                    onChange={handleChange}
                    className={`w-full p-3 border rounded-lg focus:outline-none ${
                        errors.country ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gradpath-green'
                    }`}
                />
                {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
            </div>

            {/* Personal Details */}
            <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Personal Details</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date of Birth <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            name="dateOfBirth"
                            value={formData.dateOfBirth || ''}
                            onChange={handleChange}
                            className={`w-full p-3 border rounded-lg focus:outline-none ${
                                errors.dateOfBirth ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gradpath-green'
                            }`}
                        />
                        {errors.dateOfBirth && <p className="text-red-500 text-xs mt-1">{errors.dateOfBirth}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Gender <span className="text-red-500">*</span>
                        </label>
                        <select
                            name="gender"
                            value={formData.gender || ''}
                            onChange={handleChange}
                            className={`w-full p-3 border rounded-lg focus:outline-none ${
                                errors.gender ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gradpath-green'
                            }`}
                        >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                            <option value="Prefer not to say">Prefer not to say</option>
                        </select>
                        {errors.gender && <p className="text-red-500 text-xs mt-1">{errors.gender}</p>}
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Do you have a disability?</label>
                        <select
                            name="hasDisability"
                            value={formData.hasDisability || ''}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                        >
                            <option value="Prefer not to say">Prefer not to say</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ethnicity</label>
                        <input
                            type="text"
                            name="ethnicity"
                            value={formData.ethnicity || ''}
                            onChange={handleChange}
                            placeholder="e.g. Black African, White, Coloured, Indian"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                        />
                    </div>
                </div>
            </div>

            {/* Citizenship & ID */}
            <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Citizenship & ID</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Citizenship Status</label>
                        <select
                            name="citizenship"
                            value={formData.citizenship || ''}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                        >
                            <option value="South African">South African</option>
                            <option value="Non-SA with work permit">Non-SA with work permit</option>
                            <option value="Non-SA without work permit">Non-SA without work permit</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            ID Number <span className="text-red-500">*</span>
                            <span className="text-xs text-gray-400 ml-1">(Required for South Africans)</span>
                        </label>
                        <input
                            type="text"
                            name="idNumber"
                            value={formData.idNumber || ''}
                            onChange={handleChange}
                            placeholder="13 digits"
                            className={`w-full p-3 border rounded-lg focus:outline-none ${
                                errors.idNumber ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gradpath-green'
                            }`}
                            disabled={formData.citizenship !== 'South African'}
                        />
                        {errors.idNumber && <p className="text-red-500 text-xs mt-1">{errors.idNumber}</p>}
                        {formData.citizenship !== 'South African' && (
                            <p className="text-xs text-gray-400 mt-1">ID Number not required for non-South African citizens</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Job Preferences */}
            <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Job Preferences</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            What are you looking for? <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="lookingFor"
                            value={formData.lookingFor || ''}
                            onChange={handleChange}
                            placeholder="e.g. React Developer, Software Engineering, Data Analyst"
                            className={`w-full p-3 border rounded-lg focus:outline-none ${
                                errors.lookingFor ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gradpath-green'
                            }`}
                        />
                        {errors.lookingFor && <p className="text-red-500 text-xs mt-1">{errors.lookingFor}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Preferred Job Location <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="preferredJobLocation"
                            value={formData.preferredJobLocation || ''}
                            onChange={handleChange}
                            placeholder="e.g. Johannesburg, Cape Town"
                            className={`w-full p-3 border rounded-lg focus:outline-none ${
                                errors.preferredJobLocation ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gradpath-green'
                            }`}
                        />
                        {errors.preferredJobLocation && <p className="text-red-500 text-xs mt-1">{errors.preferredJobLocation}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Desired Salary <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="desiredSalary"
                            value={formData.desiredSalary || ''}
                            onChange={handleChange}
                            placeholder="e.g. R350 000 - R450 000 per annum"
                            className={`w-full p-3 border rounded-lg focus:outline-none ${
                                errors.desiredSalary ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gradpath-green'
                            }`}
                        />
                        {errors.desiredSalary && <p className="text-red-500 text-xs mt-1">{errors.desiredSalary}</p>}
                    </div>
                    <div className="md:col-span-2">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                name="willingToRelocate"
                                checked={formData.willingToRelocate || false}
                                onChange={handleChange}
                                className="w-5 h-5 text-gradpath-green rounded focus:ring-gradpath-green"
                            />
                            <span className="text-gray-700">I am willing to relocate for the right opportunity</span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Professional Links */}
            <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Professional Links</h3>
                <p className="text-sm text-gray-500 mb-4">Add your professional profiles to help recruiters learn more about you</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            <i className="fab fa-linkedin text-blue-600 mr-1"></i> LinkedIn Profile
                            <span className="text-xs text-gray-400 ml-1">(Optional)</span>
                        </label>
                        <input
                            type="url"
                            name="linkedin"
                            value={formData.linkedin || ''}
                            onChange={handleChange}
                            placeholder="https://linkedin.com/in/yourprofile"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            <i className="fas fa-folder-open text-gradpath-green mr-1"></i> Portfolio / Website
                            <span className="text-xs text-gray-400 ml-1">(Optional)</span>
                        </label>
                        <input
                            type="url"
                            name="portfolio"
                            value={formData.portfolio || ''}
                            onChange={handleChange}
                            placeholder="https://yourportfolio.com"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                        />
                    </div>
                </div>
            </div>

            {/* Professional Summary */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Professional Summary <span className="text-red-500">*</span>
                </label>
                <textarea
                    name="professionalSummary"
                    value={formData.professionalSummary || ''}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Tell us about yourself, your career goals, and what you can offer..."
                    className={`w-full p-3 border rounded-lg focus:outline-none ${
                        errors.professionalSummary ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gradpath-green'
                    }`}
                />
                {errors.professionalSummary && <p className="text-red-500 text-xs mt-1">{errors.professionalSummary}</p>}
            </div>

            {/* Skills & Languages */}
            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Skills <span className="text-red-500">*</span>
                        <span className="text-xs text-gray-400 ml-1">(Minimum 3)</span>
                    </label>
                    <input
                        type="text"
                        value={skillsInput}
                        onChange={handleSkillsChange}
                        placeholder="JavaScript, React, Python, SQL..."
                        className={`w-full p-3 border rounded-lg focus:outline-none ${
                            errors.skills ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gradpath-green'
                        }`}
                    />
                    <p className="text-xs text-gray-400 mt-1">Separate skills with commas</p>
                    
                    {formData.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {formData.skills.map((skill, i) => (
                                <span key={i} className="bg-green-50 text-gradpath-green px-2 py-0.5 rounded text-xs">
                                    {skill}
                                </span>
                            ))}
                            <span className="text-xs text-gray-400 ml-1">({formData.skills.length} skills)</span>
                        </div>
                    )}
                    {formData.skills?.length < 3 && formData.skills?.length > 0 && (
                        <p className="text-red-500 text-xs mt-1">Add {3 - formData.skills.length} more skill(s)</p>
                    )}
                    {errors.skills && <p className="text-red-500 text-xs mt-1">{errors.skills}</p>}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Languages</label>
                    <input
                        type="text"
                        value={languagesInput}
                        onChange={handleLanguagesChange}
                        placeholder="English, Zulu, Afrikaans..."
                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                    />
                    <p className="text-xs text-gray-400 mt-1">Separate languages with commas</p>
                    
                    {formData.languages?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {formData.languages.map((lang, i) => (
                                <span key={i} className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded text-xs">
                                    {lang}
                                </span>
                            ))}
                            <span className="text-xs text-gray-400 ml-1">({formData.languages.length} languages)</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Education Section - UPDATED */}
            <div className="border-t border-gray-200 pt-6 mt-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-700">Education</h3>
                    <button
                        type="button"
                        onClick={addEducation}
                        className="text-gradpath-green hover:text-gradpath-dark text-sm font-medium"
                    >
                        <i className="fas fa-plus mr-1"></i> Add Education
                    </button>
                </div>

                {formData.education?.length === 0 && (
                    <p className="text-gray-400 text-sm italic">No education added yet</p>
                )}

                {formData.education?.map((edu, index) => (
                    <div key={edu.id} className="border border-gray-200 rounded-lg p-4 mb-4 relative">
                        <button
                            type="button"
                            onClick={() => removeEducation(index)}
                            className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                        >
                            <i className="fas fa-times"></i>
                        </button>
                        <div className="grid md:grid-cols-2 gap-3">
                            <div className="md:col-span-2">
                                <input
                                    type="text"
                                    placeholder="Institution *"
                                    value={edu.institution || ''}
                                    onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <input
                                    type="text"
                                    placeholder="Qualification / Degree *"
                                    value={edu.qualification || ''}
                                    onChange={(e) => updateEducation(index, 'qualification', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                            <div>
                                <input
                                    type="text"
                                    placeholder="Start Date (e.g., Jan 2020)"
                                    value={edu.startDate || ''}
                                    onChange={(e) => updateEducation(index, 'startDate', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                            <div>
                                <input
                                    type="text"
                                    placeholder="End Date (e.g., Dec 2024)"
                                    value={edu.endDate || ''}
                                    onChange={(e) => updateEducation(index, 'endDate', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                    disabled={edu.isCurrent}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={edu.isCurrent || false}
                                        onChange={(e) => updateEducation(index, 'isCurrent', e.target.checked)}
                                        className="w-4 h-4 text-gradpath-green rounded"
                                    />
                                    <span className="text-sm text-gray-700">I am currently studying here</span>
                                </label>
                            </div>
                            <div className="md:col-span-2">
                                <textarea
                                    placeholder="Key Highlights (e.g., Head Boy, Dean's List, Research published, etc.)"
                                    value={edu.keyHighlights || ''}
                                    onChange={(e) => updateEducation(index, 'keyHighlights', e.target.value)}
                                    rows="2"
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Work Experience Section */}
            <div className="border-t border-gray-200 pt-6 mt-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-700">Work Experience</h3>
                    <button
                        type="button"
                        onClick={addWorkExperience}
                        className="text-gradpath-green hover:text-gradpath-dark text-sm font-medium"
                    >
                        <i className="fas fa-plus mr-1"></i> Add Experience
                    </button>
                </div>

                {formData.workExperience?.length === 0 && (
                    <p className="text-gray-400 text-sm italic">No work experience added yet</p>
                )}

                {formData.workExperience?.map((exp, index) => (
                    <div key={exp.id} className="border border-gray-200 rounded-lg p-4 mb-4 relative">
                        <button
                            type="button"
                            onClick={() => removeWorkExperience(index)}
                            className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                        >
                            <i className="fas fa-times"></i>
                        </button>
                        <div className="grid md:grid-cols-2 gap-3">
                            <div className="md:col-span-2">
                                <input
                                    type="text"
                                    placeholder="Company Name *"
                                    value={exp.company || ''}
                                    onChange={(e) => updateWorkExperience(index, 'company', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <input
                                    type="text"
                                    placeholder="Job Title *"
                                    value={exp.title || ''}
                                    onChange={(e) => updateWorkExperience(index, 'title', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                            <div>
                                <input
                                    type="text"
                                    placeholder="Start Date (e.g., Jan 2022)"
                                    value={exp.startDate || ''}
                                    onChange={(e) => updateWorkExperience(index, 'startDate', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                            <div>
                                <input
                                    type="text"
                                    placeholder="End Date (e.g., Dec 2023)"
                                    value={exp.endDate || ''}
                                    onChange={(e) => updateWorkExperience(index, 'endDate', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                    disabled={exp.isCurrent}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={exp.isCurrent || false}
                                        onChange={(e) => updateWorkExperience(index, 'isCurrent', e.target.checked)}
                                        className="w-4 h-4 text-gradpath-green rounded"
                                    />
                                    <span className="text-sm text-gray-700">I currently work here</span>
                                </label>
                            </div>
                            <div className="md:col-span-2">
                                <textarea
                                    placeholder="Key responsibilities and achievements"
                                    value={exp.description || ''}
                                    onChange={(e) => updateWorkExperience(index, 'description', e.target.value)}
                                    rows="3"
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Current Work Status */}
            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            name="currentlyWorking"
                            checked={formData.currentlyWorking || false}
                            onChange={handleChange}
                            className="w-5 h-5 text-gradpath-green rounded focus:ring-gradpath-green"
                        />
                        <span className="text-gray-700">I am currently working</span>
                    </label>
                </div>
                {formData.currentlyWorking && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Current Salary (Monthly)</label>
                        <input
                            type="text"
                            name="currentSalary"
                            value={formData.currentSalary || ''}
                            onChange={handleChange}
                            placeholder="e.g. R35 000"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                        />
                    </div>
                )}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notice Period</label>
                    <input
                        type="text"
                        name="noticePeriod"
                        value={formData.noticePeriod || ''}
                        onChange={handleChange}
                        placeholder="e.g. 1 Month"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                    />
                </div>
            </div>

            {/* Drivers Licence */}
            <div>
                <label className="flex items-center gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        name="hasDriversLicence"
                        checked={formData.hasDriversLicence || false}
                        onChange={handleChange}
                        className="w-5 h-5 text-gradpath-green rounded focus:ring-gradpath-green"
                    />
                    <span className="text-gray-700">I have a valid driver's licence</span>
                </label>
            </div>

            {/* Certifications & Awards Section - NEW */}
            <div className="border-t border-gray-200 pt-6 mt-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-700">
                        <i className="fas fa-certificate text-gradpath-green mr-2"></i>
                        Certifications & Awards
                    </h3>
                    <button
                        type="button"
                        onClick={addCertification}
                        className="text-gradpath-green hover:text-gradpath-dark text-sm font-medium"
                    >
                        <i className="fas fa-plus mr-1"></i> Add Certification
                    </button>
                </div>
                <p className="text-sm text-gray-500 mb-4">Add any certifications, awards, or recognitions you have received</p>

                {formData.certifications?.length === 0 && (
                    <p className="text-gray-400 text-sm italic">No certifications or awards added yet</p>
                )}

                {formData.certifications?.map((cert, index) => (
                    <div key={cert.id} className="border border-gray-200 rounded-lg p-4 mb-4 relative">
                        <button
                            type="button"
                            onClick={() => removeCertification(index)}
                            className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                        >
                            <i className="fas fa-times"></i>
                        </button>
                        <div className="grid md:grid-cols-2 gap-3">
                            <div className="md:col-span-2">
                                <input
                                    type="text"
                                    placeholder="Certification / Award Name *"
                                    value={cert.name || ''}
                                    onChange={(e) => updateCertification(index, 'name', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                            <div>
                                <input
                                    type="text"
                                    placeholder="Issuing Organization"
                                    value={cert.issuingOrganization || ''}
                                    onChange={(e) => updateCertification(index, 'issuingOrganization', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                            <div>
                                <input
                                    type="text"
                                    placeholder="Year Received"
                                    value={cert.year || ''}
                                    onChange={(e) => updateCertification(index, 'year', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <textarea
                                    placeholder="Description (Optional)"
                                    value={cert.description || ''}
                                    onChange={(e) => updateCertification(index, 'description', e.target.value)}
                                    rows="2"
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* References Section - NEW */}
            <div className="border-t border-gray-200 pt-6 mt-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-700">
                        <i className="fas fa-address-book text-gradpath-green mr-2"></i>
                        References
                    </h3>
                    <button
                        type="button"
                        onClick={addReference}
                        className="text-gradpath-green hover:text-gradpath-dark text-sm font-medium"
                    >
                        <i className="fas fa-plus mr-1"></i> Add Reference
                    </button>
                </div>
                <p className="text-sm text-gray-500 mb-4">Add professional references who can vouch for your skills and experience</p>

                {formData.references?.length === 0 && (
                    <p className="text-gray-400 text-sm italic">No references added yet</p>
                )}

                {formData.references?.map((ref, index) => (
                    <div key={ref.id} className="border border-gray-200 rounded-lg p-4 mb-4 relative">
                        <button
                            type="button"
                            onClick={() => removeReference(index)}
                            className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                        >
                            <i className="fas fa-times"></i>
                        </button>
                        <div className="grid md:grid-cols-2 gap-3">
                            <div className="md:col-span-2">
                                <input
                                    type="text"
                                    placeholder="Full Name *"
                                    value={ref.name || ''}
                                    onChange={(e) => updateReference(index, 'name', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                            <div>
                                <input
                                    type="text"
                                    placeholder="Job Title"
                                    value={ref.title || ''}
                                    onChange={(e) => updateReference(index, 'title', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                            <div>
                                <input
                                    type="text"
                                    placeholder="Company"
                                    value={ref.company || ''}
                                    onChange={(e) => updateReference(index, 'company', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                            <div>
                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={ref.email || ''}
                                    onChange={(e) => updateReference(index, 'email', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                            <div>
                                <input
                                    type="tel"
                                    placeholder="Phone"
                                    value={ref.phone || ''}
                                    onChange={(e) => updateReference(index, 'phone', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Actions */}
            <div className="flex gap-4 pt-4 border-t border-gray-200">
                <button
                    type="button"
                    onClick={onCancel}
                    className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-gradpath-green text-white px-6 py-3 rounded-lg hover:bg-gradpath-dark transition disabled:opacity-50"
                >
                    {saving ? <i className="fas fa-spinner fa-spin"></i> : 'Save Profile'}
                </button>
            </div>
        </form>
    );
};

export default ProfileForm;